import Base from "+/Engine/Base";
import App from "+/Engine/App";
import resolveActionParams, { resolveOneParam } from "+/Engine/Helpers/resolveActionParams";

/**
 * Symbol used as the method key for the routine's implementation method.
 *
 * Exporting this Symbol is the closest JavaScript can get to a private method shared
 * with subclasses: external code has no named property to call (.run does not exist),
 * and discovering the method requires explicitly using Object.getOwnPropertySymbols() —
 * a clear signal not to do so. Only Routine subclasses should import and use RUN.
 *
 * The only valid caller of this[RUN]() is Routine.execute(). assertValidParams() is
 * always called first inside execute(), so validation is guaranteed before execution.
 */
export const RUN = Symbol("Routine.run");


export default class Routine extends Base {

  // ----------- //
  // Static Vars //
  // ----------- //

  static #routineClassMapping = {};

  static #validRoutineParamSources = [
    "constant",
    "object",
    "params",
    "nested",
    "currentEntity",
    "currentEntityField",
    "currentUser",
  ];

  static #routinesAllowingItemSources = [
    "ForEach",
  ];

  // ---------------------- //
  // Private Static Methods //
  // ---------------------- //

  static #assertValidParamDef(eventName, routineIndex, routineType, paramName, paramDef) {
    // A transform-only field def (no source) is valid — it instructs UpdateEntities to mutate the current field value.
    if (!paramDef.source && paramDef.transform) {
      return;
    }

    if (!paramDef.source) {
      throw new Error(`event "${eventName}" routine #${routineIndex} param "${paramName}" definition must include a "source"`);
    }
    if (!this.#validRoutineParamSources.includes(paramDef.source)) {
      throw new Error(`event "${eventName}" routine #${routineIndex} param "${paramName}" has unknown "source"`);
    }
    switch (paramDef.source) {
      case "nested":
        if (!paramDef.nesting) {
          throw new Error(`event "${eventName}" routine #${routineIndex} param "${paramName}" with source "nested" must define "nesting"`);
        }
        if (paramDef.nesting === null || typeof paramDef.nesting !== "object" || Array.isArray(paramDef.nesting )) {
          throw new Error(`event "${eventName}" routine #${routineIndex} param "${paramName}" "nesting" must be an object`);
        }
        Object.keys(paramDef.nesting).forEach(nestedParmName => {
          try {
            this.#assertValidParamDef(eventName, routineIndex, routineType, nestedParmName, paramDef.nesting[nestedParmName]);
          } catch (error) {
            throw new Error(
              `in event "${eventName}" routine #${routineIndex} param "${paramName}" with source "nested": ${error.message}`,
              { cause: error },
            );
          }
        });
        break;
      case "constant":
        if (paramDef.value === null || paramDef.value === undefined) {
          throw new Error(`event "${eventName}" routine #${routineIndex} param "${paramName}" with source "constant" must define a "value"`);
        }
        break;
      case "object":
        // "object" carries a literal object payload that the helper returns opaquely — used
        // by ForEach to pass a raw paramsDef down to per-item resolution without the outer
        // routine resolving its contents. Don't recurse into the payload; its entries are
        // validated at resolveActionParams time, with the right context/entity in scope.
        if (paramDef.object === null || paramDef.object === undefined
          || typeof paramDef.object !== "object" || Array.isArray(paramDef.object)
        ) {
          throw new Error(
            `event "${eventName}" routine #${routineIndex} param "${paramName}" `
            + "with source \"object\" must define a non-null object \"object\"",
          );
        }
        break;
      case "params":
        if (!paramDef.paramName) {
          throw new Error(`event "${eventName}" routine #${routineIndex} param "${paramName}" with source "params" must define a "paramName"`);
        }
        break;
      case "currentEntity":
      case "currentEntityField":
        if (!this.#routinesAllowingItemSources.includes(routineType)) {
          throw new Error(`event "${eventName}" routine #${routineIndex} param "${paramName}" source "${paramDef.source}" is only allowed in: ${this.#routinesAllowingItemSources.join(", ")}`);
        }
        if (paramDef.source === "currentEntityField" && !paramDef.fieldName) {
          throw new Error(
            `event "${eventName}" routine #${routineIndex} param "${paramName}" `
            + `with source "${paramDef.source}" must define a "fieldName"`,
          );
        }
        break;
      case "currentUser":
        break;
    }
  }

  // --------------------- //
  // Public Static Methods //
  // --------------------- //

  static make(appName, type, params) {
    if (!this.#routineClassMapping[type]) {
      throw new Error(`unknown routine type "${type}" `);
    }
    return new this.#routineClassMapping[type](appName, params);
  }

  static register(type, classObject) {
    this.#routineClassMapping[type] = classObject;
  }

  static evaluateShouldRoutineRun(routineDef, params) {
    const isRequiredRoutine = routineDef.required;
    let shouldRoutineRun = true;

    if (routineDef.runIf) {
      shouldRoutineRun = this.evaluateCondition(routineDef.runIf, params);
    }

    if (!shouldRoutineRun && isRequiredRoutine) {
      throw new Error(`Routine ${routineDef.name} failed runIf condition but is a required routine`);
    }

    return shouldRoutineRun;
  }

  static getPath(obj, path) {
    if (!path) {
      return obj;
    }

    let current = obj;

    const traversalPath = path.split(".");
    for (const key of traversalPath) {
      if (current == null) {
        return undefined;
      }
      current = current[key];
    }

    return current;
  }

  static resolveValue(def, params) {
    if (!def) {
      return undefined;
    }

    if (def.source === "constant") {
      return def.value;
    }

    if (def.source === "params") {
      const base = params[def.paramName];
      return this.getPath(base, def.path);
    }

    if (def.source === "currentEntity") {
      return def;
    }

    if (def.source === "currentEntityField") {
      return def;
    }

    return undefined;
  }

  // Retained as a thin wrapper — internal to the module; consumers go through
  // resolveActionParams, which routes transforms via the shared Transformer registry.
  static applyTransform(value, transform, context) {
    return resolveOneParam({ source: "constant", value, transform }, context, null);
  }

  static evaluateCondition(condition, params) {
    switch (condition.operator) {

      case "exists": {
        const value = this.resolveValue(condition.value, params);
        return value !== undefined && value !== null;
      }

      case "equals": {
        const left = this.resolveValue(condition.left, params);
        const right = this.resolveValue(condition.right, params);
        return left === right;
      }

      case "gt": {
        const left = this.resolveValue(condition.left, params);
        const right = this.resolveValue(condition.right, params);
        return left > right;
      }

      case "lt": {
        const left = this.resolveValue(condition.left, params);
        const right = this.resolveValue(condition.right, params);
        return left < right;
      }

      case "gte": {
        const left = this.resolveValue(condition.left, params);
        const right = this.resolveValue(condition.right, params);
        return left >= right;
      }

      case "lte": {
        const left = this.resolveValue(condition.left, params);
        const right = this.resolveValue(condition.right, params);
        return left <= right;
      }

      case "not":
        return !this.evaluateCondition(condition.condition, params);

      case "and":
        return condition.conditions.every(c => this.evaluateCondition(c, params));

      case "or":
        return condition.conditions.some(c => this.evaluateCondition(c, params));

      default:
        throw new Error(`Unknown operator ${condition.operator}`);
    }
  }

  static assertValidUse(eventName, routine, routineIndex) {
    if (!routine.name) {
      throw new Error(`event "${eventName}" routine #${routineIndex} must declare a "name"`);
    }
    if (!routine.routineType) {
      throw new Error(`event "${eventName}" routine #${routineIndex} must declare a "routineType"`);
    }
    if (!routine.params) {
      throw new Error(`event "${eventName}" routine #${routineIndex} must declare a "params"`);
    }
    if (routine.params === null || typeof routine.params !== "object" || Array.isArray(routine.params)) {
      throw new Error(`event "${eventName}" routine #${routineIndex} "params" must be an object`);
    }
    Object.keys(routine.params).forEach(paramName => {
      this.#assertValidParamDef(eventName, routineIndex, routine.routineType, paramName, routine.params[paramName]);
    });
  }

  // ------------- //
  // Instance Vars //
  // ------------- //

  #paramSources = {};
  #appName;

  // ----------------------- //
  // Public Instance Methods //
  // ----------------------- //

  constructor(appName, paramSources) {
    super();
    this.#appName = appName;
    this.#paramSources = paramSources;
  }

  /**
   * Validates the resolved params for this routine. Every subclass MUST override this method —
   * the base implementation throws to enforce that. See typechecking.md and README.md for details.
   *
   * Receives the fully resolved params object so validation operates on actual values, not
   * param source definitions. Called automatically by execute() — do not call directly.
   */
  // eslint-disable-next-line no-unused-vars
  assertValidParams(params) {
    throw new Error(`Routine "${this.constructor.name}" must implement assertValidParams()`);
  }

  /**
   * Resolves params, asserts they are valid, then runs the routine implementation.
   * This is the only valid entry point — do not call this[RUN]() directly.
   *
   * Deliberately takes ONLY `context` (the outer event's params map). The page/action
   * contexts map is NOT threaded in: routines are sandboxed against the event's params, and
   * the developer authoring an event-and-its-routines must rely exclusively on those params
   * for caller-supplied values. Session-scoped data like the current user is reached via the
   * `currentUser` source (see resolveActionParams.js), which pulls from `App.getCurrent()`
   * rather than from any caller-supplied map.
   *
   * The one previous leak — `source: currentUser` reading `contexts.currentUser.pk` — has
   * been removed; callers MUST NOT pass a second argument here.
   */
  execute(context) {
    const params = this.#extractParamValuesFromContext(context);
    try {
      this.assertValidParams(params);
      return this[RUN](params);
    } catch (e) {
      throw new Error(`Routine "${this.constructor.name}" failed: ${e.message}`, { cause: e });
    }
  }

  /**
   * Override this Symbol-keyed method in subclasses to implement the routine's logic.
   * Use `import { RUN } from "+/Engine/Routine"` and define `[RUN](params) { ... }`.
   * Never call this[RUN]() directly — always go through execute().
   *
   * Receives only `params` (the fully resolved routine params). No `contexts` — see execute()
   * above for why. Routines that need the current user should declare a param with
   * `source: currentUser` rather than trying to read it from a caller-supplied map.
   */
  // eslint-disable-next-line no-unused-vars
  [RUN](params) {
    throw new Error(`Routine "${this.constructor.name}" must implement [RUN]`);
  }

  get app() {
    return App.get(this.#appName);
  }

  // --------------------------//
  // Private Instance Methods //
  // --------------------------//

  // Routine param resolution funnels through the shared resolveActionParams helper so
  // routine-params, action-params, and foreach sub-event params all travel the same path.
  // Passing entity=null defers `currentEntity` / `currentEntityField` source defs (they get
  // resolved later inside Foreach where an entity per iteration is in scope). No page-contexts
  // thread — `source: currentUser` reaches the current user via `App.getCurrent()` inside
  // resolveActionParams, not via a caller-supplied map.
  #extractParamValuesFromContext(context) {
    return resolveActionParams(this.#paramSources, context, null);
  }
}
